import { FC, useState } from 'react';
import { X } from 'react-feather';
import styled from 'styled-components';
import { Span } from '../../typography';
import { Button, FieldRow, Flex, Spacer, StyledDatePicker, Table } from './index';
import AssignUsersModal, { AssignableUserType } from './AssignUsersModal';

export type AssignedUserRowType = AssignableUserType & {
  startDate: Date | null;
  endDate: Date | null;
};

export const startOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(0, 0, 0, 0);
  return result;
};

export const endOfDay = (date: Date): Date => {
  const result = new Date(date);
  result.setHours(23, 59, 59, 999);
  return result;
};

const dayAfter = (date: Date): Date => {
  const result = startOfDay(date);
  result.setDate(result.getDate() + 1);
  return result;
};

export const sortByName = (a: AssignableUserType, b: AssignableUserType) =>
  `${a.firstName || ''} ${a.lastName || ''}`.trim().toLowerCase() <
  `${b.firstName || ''} ${b.lastName || ''}`.trim().toLowerCase()
    ? -1
    : 1;

export const INVALID_ACCESS_PERIOD_MESSAGE = 'The end date must be later than the start date.';

const Actions = styled.div`
  display: flex;
`;

type AssignedUsersTableProps = {
  heading: string;
  loading?: boolean;
  saving?: boolean;
  rows: AssignedUserRowType[];
  selectableUsers: AssignableUserType[];
  loadingSelectableUsers?: boolean;
  onChange: (rows: AssignedUserRowType[]) => void;
  onCancel: () => void;
  onUpdate: () => void;
  onError: (message: string) => void;
};

/**
 * Lists the users that have access to a dashboard or insight, with an editable access
 * period per user. Access starts on the start date and ends after the end date; an empty
 * date is open ended.
 */
const AssignedUsersTable: FC<AssignedUsersTableProps> = props => {
  const [openAddUserModal, setOpenAddUserModal] = useState(false);

  const setRowDate = (userId: string, field: 'startDate' | 'endDate', value: Date | null) => {
    props.onChange(
      props.rows.map(row => {
        if (row.id !== userId) return row;
        const updated = { ...row, [field]: value };
        // an end date that is no longer after the start date can't stand
        if (field === 'startDate' && !!value && !!updated.endDate && updated.endDate <= value) {
          updated.endDate = null;
        }
        return updated;
      })
    );
  };

  const COLUMNS = [
    { id: 'firstName', Header: 'Name', accessor: (row: AssignedUserRowType) => row.firstName || '' },
    { id: 'lastName', Header: 'Last Name', accessor: (row: AssignedUserRowType) => row.lastName || '' },
    { id: 'email', Header: 'Email', accessor: (row: AssignedUserRowType) => row.email },
    {
      id: 'startDate',
      Header: 'Start Date',
      sortable: false,
      accessor: (row: AssignedUserRowType) => (
        <StyledDatePicker
          isSingleDate={true}
          mb={0}
          name={`startDate-${row.id}`}
          minDate={startOfDay(new Date())}
          startDate={row.startDate || undefined}
          setStartDate={date => setRowDate(row.id, 'startDate', date)}
          setEndDate={() => null}
        />
      ),
    },
    {
      id: 'endDate',
      Header: 'End Date',
      sortable: false,
      accessor: (row: AssignedUserRowType) => (
        <StyledDatePicker
          isSingleDate={true}
          mb={0}
          name={`endDate-${row.id}`}
          minDate={dayAfter(row.startDate || new Date())}
          startDate={row.endDate || undefined}
          setStartDate={date => setRowDate(row.id, 'endDate', date)}
          setEndDate={() => null}
        />
      ),
    },
    {
      id: 'actions',
      Header: '',
      sortable: false,
      width: 60,
      accessor: (row: AssignedUserRowType) => (
        <Flex direction="row" size="1" justify="flex-end">
          <Button
            bg="error"
            icon={<X />}
            isIcon={true}
            p={0}
            type="button"
            width="40px"
            onClick={() => props.onChange(props.rows.filter(item => item.id !== row.id))}
          />
        </Flex>
      ),
    },
  ];

  return (
    <Flex direction="column" size="1" width="100%">
      <AssignUsersModal
        open={openAddUserModal}
        loading={props.loadingSelectableUsers}
        users={props.selectableUsers}
        onClose={() => setOpenAddUserModal(false)}
        onAdd={users =>
          props.onChange(
            [...props.rows, ...users.map(user => ({ ...user, startDate: null, endDate: null }))].sort(sortByName)
          )
        }
      />
      <Table
        hideActionButton={true}
        heading={props.heading}
        columns={COLUMNS}
        loading={props.loading}
        data={props.rows}
        defaultPageSize={10}
        filters={
          <Flex direction="row" size="1" justify="flex-end" alignItems="center">
            <Button type="button" text="+ User" width="auto" onClick={() => setOpenAddUserModal(true)} />
          </Flex>
        }
      />
      <Spacer spacing={10} />
      <Span color="textDefault" px={4}>
        Access starts on the start date and ends after the end date. Leave a date empty for open ended access.
      </Span>
      <Spacer spacing={20} />
      <FieldRow justifyContent="flex-end" px={4}>
        <Flex direction="row" size="1" justify="flex-end">
          <Actions>
            <Button type="button" text="Cancel" mr={3} onClick={() => props.onCancel()} />
            <Button
              type="button"
              text="Update"
              isLoading={props.saving}
              disabled={props.saving}
              onClick={() => {
                const invalid = props.rows.find(
                  row => !!row.startDate && !!row.endDate && row.endDate <= row.startDate
                );
                if (!!invalid) {
                  props.onError(INVALID_ACCESS_PERIOD_MESSAGE);
                  return;
                }
                props.onUpdate();
              }}
            />
          </Actions>
        </Flex>
      </FieldRow>
    </Flex>
  );
};

export default AssignedUsersTable;
